import InputMap from 'login-components/Common/Inputs/InputMap';
import * as React from 'react';
import FormDialog from 'login-components/Common/Form/FormDialog/FormDialog';
import { Field, FormProps, reduxForm } from 'redux-form';
import FormDialogFooter from 'login-components/Common/Form/FormDialog/FormDialogFooter';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import * as formUtils from 'login-utils/formUtils';
import AppLayoutContextUpdater from 'login-components/Common/AppLayoutContextUpdater/AppLayoutContextUpdater';
import MessageBar from 'login-components/Common/MessageBar/MessageBar';
import SubTitle from 'login-components/Common/SubTitle/SubTitle';
import { hashHistory } from 'react-router';
import { setupNewMFA } from 'login-apis/MFAAPI';
import { StatesRich } from 'login-constants/states';
import './MFASetup.scss';

const phoneNumberInput: FormInput = {
  ...InputMap.PhoneInput,
  name: 'phoneNumber',
  identifier: 'phone number',
};
const FORM_NAME = 'MFASetup';
const FORM_ERROR_ID = `${FORM_NAME}-formError`;
const deliveryRadioOptions = [
  {
    text: 'Text Message',
    value: 'sms',
  },
  {
    text: 'Voice Call',
    value: 'call',
  },
];

type DeliveryMethod = 'sms' | 'call' | null;

interface MFAFormProps extends ReactRouter.RouteComponentProps<{}, {}> {}
interface MFAFormProps extends FormProps<any, any, any> {}
interface FormData {
  phoneNumber: string;
  deliveryMethod: DeliveryMethod;
}

interface MFAFormState {
  errorMessage: string | null;
}

export class MFASetup extends React.Component<MFAFormProps, MFAFormState> {
  constructor(props) {
    super(props);
    this.state = {
      errorMessage: null,
    };
  }

  submitForm = async (formData: FormData) => {
    const { phoneNumber, deliveryMethod } = formData;
    const generalErrorMessage = 'Something went wrong.';
    if (deliveryMethod) {
      try {
        const res = await setupNewMFA({
          phoneNumber,
          deliveryMethod,
        });
        if (res.data && res.data._id) {
          const mfaId = res.data._id;
          hashHistory.push(`/${StatesRich.LENDER_MFA_VALIDATE.path}/?mfaId=${mfaId}`);
        } else {
          throw new Error();
        }
      } catch (e) {
        this.setState({
          errorMessage: generalErrorMessage,
        });
      }
    }
  };

  render() {
    const appTitle = '2-Step Verification';
    const actionText = 'Send code';
    const { handleSubmit, submitting } = this.props;
    const { errorMessage } = this.state;
    return (
      <div className="MFASetup" id="it-MFASetup">
        <AppLayoutContextUpdater appTitle={appTitle} footerConfig={null} appDocumentTitle={appTitle}>
          <div className="MFASetup-dialog" id="it-MFASetup-dialog">
            <form id="it-MFASetupForm" onSubmit={handleSubmit!(this.submitForm)}>
              <FormDialog>
                <div className="MFASetup-dialogBody">
                  {errorMessage && (
                    <MessageBar messageText={errorMessage} messageType="error" id="it-MFASetup-MessageBar" />
                  )}
                  <SubTitle>
                    For improved security, you’ll be <strong>asked for a code</strong> upon login that will be
                    <strong> sent to your mobile device</strong>.
                  </SubTitle>
                  <fieldset>
                    <div className="MFASetup-inputLabelWrapper">
                      <label htmlFor={phoneNumberInput.name}>
                        <strong>Send the code to:</strong>
                      </label>
                    </div>
                    <Field key={phoneNumberInput.name} placeholder="Mobile Number" {...phoneNumberInput} />
                  </fieldset>
                  <fieldset>
                    <div className="MFASetup-methodRadiosWrapper">
                      <legend id="deliveryMethod">
                        <div className="MFASetup-inputLabelWrapper MFASetup-deliveryMethodsWrapper">
                          <strong>How do you want to receive your code?</strong>
                        </div>
                      </legend>
                      <Field
                        key="deliveryRadio"
                        name="deliveryMethod"
                        options={deliveryRadioOptions}
                        inline={true}
                        {...InputMap.RadioGroupInput}
                      />
                    </div>
                  </fieldset>
                </div>
                <FormDialogFooter>
                  <PrimaryButton buttonId="it-MFASetup-submitButton" disabled={submitting} text={actionText} />
                </FormDialogFooter>
              </FormDialog>
            </form>
          </div>
        </AppLayoutContextUpdater>
      </div>
    );
  }
}

function mfaFormValidate(values) {
  const validationRes = formUtils.validate([phoneNumberInput], values);
  if (!values.deliveryMethod) {
    validationRes.deliveryMethod = 'Please select a delivery method.';
  }
  return validationRes;
}

const MFASetupForm = reduxForm<any, any>({
  form: FORM_NAME,
  validate: mfaFormValidate as any,
  onSubmitFail: syncErrors => formUtils.focusOnSubmitFail(syncErrors, FORM_ERROR_ID),
})(MFASetup);

export default MFASetupForm;
